import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

import PageContainer from "../../../components/PageContainer";
import {
  claimChatConversation,
  closeChatConversation,
  getChatConversations,
  getChatMessages,
  markChatConversationRead,
  replyChatConversation,
} from "../../../services/modules/ChatInbox";

const formatDT = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const ChatInbox = () => {
  const [tab, setTab] = useState("pending"); // pending | mine
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");

  const pollRef = useRef(null);
  const msgPollRef = useRef(null);

  const selectedConversation = useMemo(
    () => conversations.find((c) => String(c.id_conversation) === String(selectedId)) || null,
    [conversations, selectedId]
  );

  const fetchConversations = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const data = await getChatConversations({ view: tab, limit: 80 });
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar el inbox.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMessages = async ({ idConversation, silent = false } = {}) => {
    if (!idConversation) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const data = await getChatMessages(idConversation, { limit: 500 });
      setMessages(Array.isArray(data) ? data : []);
      await markChatConversationRead(idConversation).catch(() => {});
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los mensajes.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchConversations({ silent: true }), 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    setMessages([]);
    if (msgPollRef.current) clearInterval(msgPollRef.current);
    if (!selectedId) return;
    fetchMessages({ idConversation: selectedId });
    msgPollRef.current = setInterval(() => fetchMessages({ idConversation: selectedId, silent: true }), 2500);
    return () => {
      if (msgPollRef.current) clearInterval(msgPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const totalUnread = useMemo(() => {
    return (Array.isArray(conversations) ? conversations : []).reduce(
      (acc, c) => acc + Number(c?.unread_count || 0),
      0
    );
  }, [conversations]);

  const handleClaim = async () => {
    if (!selectedId) return;
    setSending(true);
    setError("");
    try {
      await claimChatConversation(selectedId);
      setTab("mine");
      await fetchConversations();
    } catch (e) {
      console.error(e);
      setError("No se pudo tomar la conversación (quizá otro usuario la tomó).");
      await fetchConversations({ silent: true });
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const text = String(draft || "").trim();
    if (!text || !selectedId) return;
    setSending(true);
    setError("");
    try {
      await replyChatConversation(selectedId, text);
      setDraft("");
      await fetchMessages({ idConversation: selectedId, silent: true });
      await fetchConversations({ silent: true });
    } catch (e) {
      console.error(e);
      setError("No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selectedId) return;
    setSending(true);
    setError("");
    try {
      await closeChatConversation(selectedId);
      setSelectedId(null);
      setMessages([]);
      await fetchConversations();
    } catch (e) {
      console.error(e);
      setError("No se pudo cerrar la conversación.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageContainer>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <ChatIcon />
          <Typography variant="h5" fontWeight={700}>
            Inbox Telegram
          </Typography>
        </Stack>
        <Badge color="error" badgeContent={totalUnread} max={99}>
          <Box />
        </Badge>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => {
            setSelectedId(null);
            setTab(v);
          }}
        >
          <Tab
            value="pending"
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography>Solicitudes</Typography>
              </Stack>
            }
          />
          <Tab value="mine" label="Mis conversaciones" />
        </Tabs>
        <Divider />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px 1fr" }, minHeight: 560 }}>
          <Box sx={{ borderRight: { md: "1px solid", xs: "0" }, borderColor: "divider" }}>
            <List dense sx={{ py: 0 }}>
              {conversations.length ? (
                conversations.map((c) => {
                  const isSelected = String(c.id_conversation) === String(selectedId);
                  const primary = c.visitor_name ? `${c.visitor_name}` : `Chat ${c.external_chat_id}`;
                  const secondaryParts = [];
                  if (c.visitor_phone) secondaryParts.push(c.visitor_phone);
                  if (c.last_message_at) secondaryParts.push(formatDT(c.last_message_at));
                  const secondary = secondaryParts.join(" · ");
                  return (
                    <ListItemButton
                      key={c.id_conversation}
                      selected={isSelected}
                      onClick={() => setSelectedId(c.id_conversation)}
                      sx={{ alignItems: "flex-start" }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography fontWeight={700} noWrap>
                              {primary}
                            </Typography>
                            {Number(c.unread_count || 0) > 0 ? (
                              <Badge color="error" badgeContent={Number(c.unread_count || 0)} max={99} />
                            ) : null}
                          </Stack>
                        }
                        secondary={secondary}
                      />
                    </ListItemButton>
                  );
                })
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    {loading ? "Cargando..." : "No hay conversaciones en esta vista."}
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {selectedConversation ? (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography fontWeight={800}>
                      {selectedConversation.visitor_name || `Chat ${selectedConversation.external_chat_id}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedConversation.visitor_phone ? `${selectedConversation.visitor_phone} · ` : ""}
                      {selectedConversation.status}
                    </Typography>
                  </Box>

                  <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end">
                    {tab === "pending" ? (
                      <Button
                        variant="contained"
                        startIcon={<AssignmentIndIcon />}
                        onClick={handleClaim}
                        disabled={sending}
                      >
                        Tomar
                      </Button>
                    ) : null}
                    {tab === "mine" ? (
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        color="error"
                        onClick={handleClose}
                        disabled={sending}
                      >
                        Cerrar
                      </Button>
                    ) : null}
                    <Button
                      variant="text"
                      startIcon={<DoneAllIcon />}
                      onClick={() => (selectedId ? fetchMessages({ idConversation: selectedId }) : null)}
                      disabled={sending}
                    >
                      Actualizar
                    </Button>
                  </Stack>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, flex: 1, overflow: "auto", bgcolor: "background.default" }}>
                  <Stack gap={1}>
                    {messages.map((m) => {
                      const mine = m.sender_type === "agent";
                      const isSystem = m.sender_type === "system";
                      return (
                        <Box
                          key={m.id_message}
                          sx={{
                            display: "flex",
                            justifyContent: isSystem ? "center" : mine ? "flex-end" : "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: "78%",
                              px: 1.5,
                              py: 1,
                              borderRadius: 2,
                              bgcolor: isSystem ? "transparent" : mine ? "primary.main" : "grey.100",
                              color: isSystem ? "text.secondary" : mine ? "primary.contrastText" : "text.primary",
                              border: isSystem ? "1px dashed" : "1px solid",
                              borderColor: isSystem ? "divider" : "transparent",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            <Typography variant="body2">{m.message_text}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              {formatDT(m.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>

                <Stack direction="row" gap={1}>
                  <TextField
                    fullWidth
                    placeholder={tab === "mine" ? "Escribe tu respuesta..." : "Toma la conversación para responder"}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (tab === "mine") handleSend();
                      }
                    }}
                    multiline
                    minRows={1}
                    maxRows={4}
                    disabled={sending || tab !== "mine"}
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={sending || tab !== "mine" || !String(draft || "").trim()}
                  >
                    Enviar
                  </Button>
                </Stack>
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">Selecciona una conversación para ver los mensajes.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default ChatInbox;

